import { useMemo, useState } from "react";
import { PROVINCES, findProvince } from "../lib/provinces";
import type { DataKind, TeamForm as TeamFormValues } from "../lib/types";

const EMPTY: TeamFormValues = {
  provinceName: "",
  provinceSlug: "",
  teamName: "",
  contactName: "",
  email: "",
  phone: "",
  dataKind: "rgb",
  droneModel: "",
  cameraModel: "",
  note: "",
};

type Errors = Partial<Record<keyof TeamFormValues, string>>;

function validate(v: TeamFormValues): Errors {
  const e: Errors = {};
  if (!v.provinceName) e.provinceName = "İl seçiniz.";
  if (v.teamName.trim().length < 2) e.teamName = "Ekip / birim adı giriniz.";
  if (v.contactName.trim().length < 3) e.contactName = "Ad soyad giriniz.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = "Geçerli bir e-posta giriniz.";
  if (v.phone.replace(/\D/g, "").length < 10) e.phone = "Geçerli bir telefon giriniz.";
  return e;
}

export function TeamForm({ onSubmit }: { onSubmit: (values: TeamFormValues) => void }) {
  const [values, setValues] = useState<TeamFormValues>(EMPTY);
  const [touched, setTouched] = useState(false);

  // Derived rather than stored: keeping errors in state meant every setter had
  // to remember to recompute them, and a stale copy of `values` could be
  // written back, silently dropping a field the user had already filled.
  const errors: Errors = useMemo(() => (touched ? validate(values) : {}), [touched, values]);

  function set<K extends keyof TeamFormValues>(key: K, value: TeamFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(validate(values)).length === 0) onSubmit(values);
  }

  const err = (k: keyof TeamFormValues) =>
    errors[k] ? <span className="err">{errors[k]}</span> : null;

  return (
    <form className="card" onSubmit={handleSubmit} noValidate>
      <h2>1. Ekip bilgileri</h2>
      <p className="muted">
        Teşvik hediyesinin ulaştırılabilmesi için iletişim bilgileri zorunludur.
      </p>

      <div className="grid">
        <label>
          İl <span className="req">*</span>
          <select
            value={values.provinceName}
            onChange={(e) => {
              const p = findProvince(e.target.value);
              setValues((prev) => ({
                ...prev,
                provinceName: p?.name ?? "",
                provinceSlug: p?.slug ?? "",
              }));
            }}
          >
            <option value="">— İl seçiniz —</option>
            {PROVINCES.map((p) => (
              <option key={p.slug} value={p.name}>
                {String(p.code).padStart(2, "0")} · {p.name}
              </option>
            ))}
          </select>
          {err("provinceName")}
        </label>

        <label>
          Ekip / birim adı <span className="req">*</span>
          <input
            value={values.teamName}
            onChange={(e) => set("teamName", e.target.value)}
            placeholder="Örn. Ankara İl AFAD Müdürlüğü Drone Ekibi"
          />
          {err("teamName")}
        </label>

        <label>
          Ad soyad <span className="req">*</span>
          <input
            value={values.contactName}
            onChange={(e) => set("contactName", e.target.value)}
            autoComplete="name"
          />
          {err("contactName")}
        </label>

        <label>
          E-posta <span className="req">*</span>
          <input
            type="email"
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            autoComplete="email"
          />
          {err("email")}
        </label>

        <label>
          Telefon <span className="req">*</span>
          <input
            type="tel"
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="05XX XXX XX XX"
            autoComplete="tel"
          />
          {err("phone")}
        </label>

        <label>
          Veri tipi <span className="req">*</span>
          <select
            value={values.dataKind}
            onChange={(e) => set("dataKind", e.target.value as DataKind)}
          >
            <option value="rgb">RGB görüntüler</option>
            <option value="thermal">Termal görüntüler</option>
          </select>
          <span className="hint">
            RGB ve termal setler ayrı ayrı yüklenir. Her biri için bu formu tekrar doldurunuz.
          </span>
        </label>

        <label>
          Drone modeli
          <input
            value={values.droneModel}
            onChange={(e) => set("droneModel", e.target.value)}
            placeholder="Örn. DJI Matrice 30T"
          />
        </label>

        <label>
          Kamera modeli
          <input
            value={values.cameraModel}
            onChange={(e) => set("cameraModel", e.target.value)}
            placeholder="Örn. H20T"
          />
        </label>
      </div>

      <label className="full">
        Not / açıklama
        <textarea
          rows={3}
          value={values.note}
          onChange={(e) => set("note", e.target.value)}
          placeholder="Çekim alanı, tarih aralığı veya iletmek istediğiniz diğer bilgiler"
        />
      </label>

      <button type="submit" className="primary">
        Devam et →
      </button>
    </form>
  );
}
